import { KARTE_ACCOUNT_SETTINGS_PATH, KARTE_PROJECT_SETTINGS_PATH } from '../config/const/karte.js';
import type { Platform } from '../contract/index.js';
import { AUTH_ROUTE_TEMPLATES, fillAuthRouteTemplate, resolveAuthHref } from './authRoutes.js';
import type { HrefBuilder } from './urls.js';

const buildKarteProjectSettingsHref = (params: {
  buildHref: HrefBuilder;
  karteOrganizationId?: string;
  karteProjectId: string;
}): { href: string; crossDomain: boolean } => {
  const { buildHref, karteOrganizationId, karteProjectId } = params;
  const { href, crossDomain } = buildHref({
    host: 'karte-io',
    path: KARTE_PROJECT_SETTINGS_PATH,
    skipProjectParam: true,
  });
  const base = crossDomain ? href : `${typeof window === 'undefined' ? 'http://__internal__' : window.location.origin}${href}`;
  const url = new URL(base);
  // org を省くと KARTE 基盤は cookie `krt_current_org_id` から組織を補完する (project からは逆引きしない)
  // ため、直前に別組織を見ていた利用者がアクセス拒否になる。
  if (karteOrganizationId) url.searchParams.set('org', karteOrganizationId);
  url.searchParams.set('project', karteProjectId);
  return {
    href: crossDomain ? url.toString() : `${url.pathname}${url.search}${url.hash}`,
    crossDomain,
  };
};

/**
 * ユーザーメニューの「アカウント設定」飛び先。
 * 表示中の基盤 (KARTE / QANT) 側の設定画面を開く。
 */
export function resolveAccountSettingsHref(params: { buildHref: HrefBuilder; selfPlatform: Platform }): {
  href: string;
  crossDomain: boolean;
} {
  const { buildHref, selfPlatform } = params;

  if (selfPlatform === 'karte') {
    return buildHref({
      host: 'karte-io',
      path: KARTE_ACCOUNT_SETTINGS_PATH,
      skipProjectParam: true,
    });
  }

  return resolveAuthHref(AUTH_ROUTE_TEMPLATES.accountProfile, buildHref);
}

/**
 * プロジェクト切替 Popover の「プロジェクト設定」飛び先。
 *
 * - KARTE ホスト: `karteProjectId` がある行だけ KARTE 設定（mapping 由来の id も含む。権限は見ない）
 * - QANT ホスト: `qantProjectId` があり `hasProjectSettingsAccess` がある行だけ QANT 設定
 */
export function resolveProjectSettingsHref(params: {
  buildHref: HrefBuilder;
  selfPlatform: Platform;
  qantOrganizationId?: string;
  qantProjectId?: string;
  karteOrganizationId?: string;
  karteProjectId?: string;
  hasProjectSettingsAccess?: boolean;
}): { href: string; crossDomain: boolean } | undefined {
  const { buildHref, selfPlatform, qantOrganizationId, qantProjectId, karteOrganizationId, karteProjectId, hasProjectSettingsAccess } =
    params;

  if (selfPlatform === 'karte' && karteProjectId) {
    return buildKarteProjectSettingsHref({ buildHref, karteOrganizationId, karteProjectId });
  }

  if (selfPlatform === 'qant' && qantOrganizationId && qantProjectId && hasProjectSettingsAccess) {
    return resolveAuthHref(
      fillAuthRouteTemplate(AUTH_ROUTE_TEMPLATES.projectSettings, {
        organizationId: qantOrganizationId,
        projectId: qantProjectId,
      }),
      buildHref,
    );
  }

  return undefined;
}
